import React, { useState, useEffect, useMemo } from 'react';
import { databaseService } from '../../services/databaseService';
import styles from './Dashboard.module.css';

const DatabaseSummary = () => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedSchemas, setExpandedSchemas] = useState({});
  const [selectedTable, setSelectedTable] = useState(null);
  const [tableData, setTableData] = useState(null);
  const [loadingTableData, setLoadingTableData] = useState(false);
  const [tableDataPage, setTableDataPage] = useState(0);
  const [tableDataPageSize] = useState(50);
  const [tableSearch, setTableSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState(null);

  useEffect(() => {
    loadDatabaseSummary();
  }, []);

  // Table data page değiştiğinde yeniden yükle
  useEffect(() => {
    if (selectedTable && tableDataPage >= 0) {
      loadTableData(selectedTable.schema, selectedTable.table);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableDataPage]);

  const loadDatabaseSummary = async () => {
    try {
      setLoading(true);
      setError(null);

      // Tüm şemaları al
      const schemas = await databaseService.listSchemas();
      
      const summaryData = {
        totalSchemas: schemas.length,
        totalTables: 0,
        schemas: [],
        dataTypes: {
          parks: [],
          greenAreas: [],
          geometries: [],
          waterConsumption: [],
          coordinates: [],
        },
      };

      // Her şema için tabloları ve kolonları kontrol et
      for (const schema of schemas) {
        try {
          const tables = await databaseService.listTables(schema);
          summaryData.totalTables += tables.length;

          const schemaData = {
            name: schema,
            tableCount: tables.length,
            tables: [],
          };

          // Her tablo için kolonları kontrol et
          for (const table of tables.slice(0, 50)) { // İlk 50 tablo (performans için)
            try {
              const structure = await databaseService.getTableStructure(schema, table);
              const columns = structure.map(col => ({
                name: col.column_name,
                type: col.data_type,
                nullable: col.is_nullable === 'YES',
              }));

              // Veri türlerini tespit et
              const hasParkData = columns.some(col => 
                col.name.toLowerCase().includes('park') ||
                col.name.toLowerCase().includes('ismi') ||
                col.name.toLowerCase().includes('name')
              );

              const hasGreenAreaData = columns.some(col => 
                col.name.includes('ÇİM') ||
                col.name.includes('SERT') ||
                col.name.includes('KALDIRIM') ||
                col.name.toLowerCase().includes('green') ||
                col.name.toLowerCase().includes('area')
              );

              const hasGeometryData = columns.some(col => 
                col.type === 'geometry' ||
                col.type === 'geography' ||
                col.name.toLowerCase().includes('geometry') ||
                col.name.toLowerCase().includes('geom') ||
                col.name.toLowerCase().includes('wkt') ||
                col.name.toLowerCase().includes('shape')
              );

              const hasCoordinatesData = columns.some(col => 
                col.name.toLowerCase().includes('lat') ||
                col.name.toLowerCase().includes('lng') ||
                col.name.toLowerCase().includes('lon') ||
                col.name.toLowerCase().includes('coordinate') ||
                col.name.toLowerCase().includes('x') && col.name.toLowerCase().includes('y')
              );

              const hasWaterData = columns.some(col => 
                col.name.toLowerCase().includes('su') ||
                col.name.toLowerCase().includes('water') ||
                col.name.toLowerCase().includes('tüketim') ||
                col.name.toLowerCase().includes('consumption')
              );

              // Kayıt sayısını al (hızlı sorgu)
              let rowCount = 0;
              try {
                const data = await databaseService.getTableData(schema, table, 1, 0);
                rowCount = data.totalCount || 0;
              } catch (e) {
                // Kayıt sayısı alınamazsa 0
              }

              const tableInfo = {
                name: table,
                columnCount: columns.length,
                rowCount: rowCount,
                columns: columns.slice(0, 20), // İlk 20 kolon (performans için)
                hasParkData,
                hasGreenAreaData,
                hasGeometryData,
                hasCoordinatesData,
                hasWaterData,
              };

              schemaData.tables.push(tableInfo);

              // Veri türlerine göre kategorize et
              if (hasParkData) {
                summaryData.dataTypes.parks.push(`${schema}.${table}`);
              }
              if (hasGreenAreaData) {
                summaryData.dataTypes.greenAreas.push(`${schema}.${table}`);
              }
              if (hasGeometryData) {
                summaryData.dataTypes.geometries.push(`${schema}.${table}`);
              }
              if (hasCoordinatesData) {
                summaryData.dataTypes.coordinates.push(`${schema}.${table}`);
              }
              if (hasWaterData) {
                summaryData.dataTypes.waterConsumption.push(`${schema}.${table}`);
              }
            } catch (err) {
              console.warn(`Tablo ${schema}.${table} yapısı alınamadı:`, err.message);
            }
          }

          summaryData.schemas.push(schemaData);
        } catch (err) {
          console.warn(`Şema ${schema} tabloları alınamadı:`, err.message);
        }
      }

      setSummary(summaryData);
    } catch (err) {
      console.error('Veritabanı özeti yüklenemedi:', err);
      setError(err.message || 'Veritabanı özeti yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const toggleSchema = (schemaName) => {
    setExpandedSchemas(prev => ({
      ...prev,
      [schemaName]: !prev[schemaName],
    }));
  };

  const loadTableData = async (schema, table) => {
    try {
      setLoadingTableData(true);
      setSelectedTable({ schema, table });
      
      console.log('Tablo verisi yükleniyor:', { schema, table });
      
      const data = await databaseService.getTableData(
        schema,
        table,
        tableDataPageSize,
        tableDataPage * tableDataPageSize
      );
      
      console.log('Tablo verisi yüklendi:', { 
        rowCount: data.rows.length, 
        totalCount: data.totalCount,
        columns: data.columns.length 
      });
      
      setTableData(data);
    } catch (err) {
      console.error('Tablo verisi yüklenemedi:', err);
      console.error('Hata detayları:', { schema, table, error: err.message });
      setTableData(null);
    } finally {
      setLoadingTableData(false);
    }
  };

  const closeTableData = () => {
    setSelectedTable(null);
    setTableData(null);
    setTableDataPage(0);
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div className={styles.spinner}></div>
        <p style={{ marginTop: '20px', color: '#64748b' }}>Veritabanı özeti yükleniyor...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <p style={{ color: '#ef4444' }}>Hata: {error}</p>
        <button
          onClick={loadDatabaseSummary}
          style={{
            marginTop: '20px',
            padding: '10px 20px',
            backgroundColor: '#3b82f6',
            color: '#ffffff',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
          }}
        >
          Tekrar Dene
        </button>
      </div>
    );
  }

  if (!summary) {
    return null;
  }

  return (
    <div style={{ padding: '32px' }}>
      <h2 style={{ marginBottom: '24px', color: '#1e293b' }}>Veritabanı Özeti</h2>

      {/* Genel İstatistikler */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '16px',
        marginBottom: '32px',
      }}>
        <div style={{
          background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
          padding: '20px',
          borderRadius: '12px',
          color: '#ffffff',
        }}>
          <div style={{ fontSize: '32px', fontWeight: '700' }}>{summary.totalSchemas}</div>
          <div style={{ fontSize: '14px', opacity: 0.9 }}>Şema</div>
        </div>
        <div style={{
          background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
          padding: '20px',
          borderRadius: '12px',
          color: '#ffffff',
        }}>
          <div style={{ fontSize: '32px', fontWeight: '700' }}>{summary.totalTables}</div>
          <div style={{ fontSize: '14px', opacity: 0.9 }}>Tablo</div>
        </div>
        <div style={{
          background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
          padding: '20px',
          borderRadius: '12px',
          color: '#ffffff',
        }}>
          <div style={{ fontSize: '32px', fontWeight: '700' }}>{summary.dataTypes.parks.length}</div>
          <div style={{ fontSize: '14px', opacity: 0.9 }}>Park Verisi</div>
        </div>
        <div style={{
          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          padding: '20px',
          borderRadius: '12px',
          color: '#ffffff',
        }}>
          <div style={{ fontSize: '32px', fontWeight: '700' }}>{summary.dataTypes.greenAreas.length}</div>
          <div style={{ fontSize: '14px', opacity: 0.9 }}>Yeşil Alan Verisi</div>
        </div>
        <div style={{
          background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
          padding: '20px',
          borderRadius: '12px',
          color: '#ffffff',
        }}>
          <div style={{ fontSize: '32px', fontWeight: '700' }}>{summary.dataTypes.geometries.length}</div>
          <div style={{ fontSize: '14px', opacity: 0.9 }}>Şekil/Geometri Verisi</div>
        </div>
        <div style={{
          background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
          padding: '20px',
          borderRadius: '12px',
          color: '#ffffff',
        }}>
          <div style={{ fontSize: '32px', fontWeight: '700' }}>{summary.dataTypes.waterConsumption.length}</div>
          <div style={{ fontSize: '14px', opacity: 0.9 }}>Su Tüketimi Verisi</div>
        </div>
      </div>

      {/* Veri Türleri Detayları */}
      <div style={{ marginBottom: '32px' }}>
        <h3 style={{ marginBottom: '16px', color: '#1e293b' }}>Veri Türleri</h3>
        
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '16px',
        }}>
          {summary.dataTypes.greenAreas.length > 0 && (
            <div style={{
              background: '#f8fafc',
              padding: '16px',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
            }}>
              <h4 style={{ margin: '0 0 12px 0', color: '#10b981' }}>Yeşil Alan Verileri</h4>
              <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '13px', color: '#64748b' }}>
                {summary.dataTypes.greenAreas.map((table, idx) => (
                  <li key={idx}>{table}</li>
                ))}
              </ul>
            </div>
          )}

          {summary.dataTypes.geometries.length > 0 && (
            <div style={{
              background: '#f8fafc',
              padding: '16px',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
            }}>
              <h4 style={{ margin: '0 0 12px 0', color: '#8b5cf6' }}>Şekil/Geometri Verileri</h4>
              <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '13px', color: '#64748b' }}>
                {summary.dataTypes.geometries.map((table, idx) => (
                  <li key={idx}>{table}</li>
                ))}
              </ul>
            </div>
          )}

          {summary.dataTypes.parks.length > 0 && (
            <div style={{
              background: '#f8fafc',
              padding: '16px',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
            }}>
              <h4 style={{ margin: '0 0 12px 0', color: '#f59e0b' }}>Park Verileri</h4>
              <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '13px', color: '#64748b' }}>
                {summary.dataTypes.parks.map((table, idx) => (
                  <li key={idx}>{table}</li>
                ))}
              </ul>
            </div>
          )}

          {summary.dataTypes.waterConsumption.length > 0 && (
            <div style={{
              background: '#f8fafc',
              padding: '16px',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
            }}>
              <h4 style={{ margin: '0 0 12px 0', color: '#06b6d4' }}>Su Tüketimi Verileri</h4>
              <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '13px', color: '#64748b' }}>
                {summary.dataTypes.waterConsumption.map((table, idx) => (
                  <li key={idx}>{table}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Şemalar ve Tablolar */}
      <div>
        <h3 style={{ marginBottom: '16px', color: '#1e293b' }}>Tum Semalar ve Tablolar</h3>

        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
          <input
            type="text"
            placeholder="Tablo adi ara..."
            value={tableSearch}
            onChange={e => setTableSearch(e.target.value)}
            style={{ flex: 1, maxWidth: 300, padding: '7px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, outline: 'none', color: '#1e293b' }}
          />
          {[
            { key: 'park', label: 'Park', bg: '#fef3c7', color: '#92400e', field: 'hasParkData' },
            { key: 'green', label: 'Yesil Alan', bg: '#d1fae5', color: '#065f46', field: 'hasGreenAreaData' },
            { key: 'geo', label: 'Sekil', bg: '#e9d5ff', color: '#6b21a8', field: 'hasGeometryData' },
            { key: 'water', label: 'Su', bg: '#cffafe', color: '#164e63', field: 'hasWaterData' },
          ].map(cat => (
            <button
              key={cat.key}
              onClick={() => setCategoryFilter(prev => prev === cat.key ? null : cat.key)}
              style={{
                padding: '4px 10px', borderRadius: 4, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                background: categoryFilter === cat.key ? cat.color : cat.bg,
                color: categoryFilter === cat.key ? '#fff' : cat.color,
                border: `1px solid ${categoryFilter === cat.key ? cat.color : 'transparent'}`,
                transition: 'all 0.15s',
              }}
            >{cat.label}</button>
          ))}
          {(tableSearch || categoryFilter) && (
            <button onClick={() => { setTableSearch(''); setCategoryFilter(null); }} style={{ padding: '4px 10px', borderRadius: 4, fontSize: 11, border: '1px solid #e2e8f0', background: '#fff', color: '#64748b', cursor: 'pointer' }}>Temizle</button>
          )}
        </div>
        
        {summary.schemas.map((schema) => {
          const categoryFieldMap = { park: 'hasParkData', green: 'hasGreenAreaData', geo: 'hasGeometryData', water: 'hasWaterData' };
          const filteredTables = schema.tables.filter(t => {
            if (tableSearch && !t.name.toLowerCase().includes(tableSearch.toLowerCase())) return false;
            if (categoryFilter && !t[categoryFieldMap[categoryFilter]]) return false;
            return true;
          });
          if (filteredTables.length === 0 && (tableSearch || categoryFilter)) return null;
          return (<div
            key={schema.name}
            style={{
              marginBottom: '16px',
              background: '#ffffff',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              overflow: 'hidden',
            }}
          >
            <button
              onClick={() => toggleSchema(schema.name)}
              style={{
                width: '100%',
                padding: '16px',
                background: '#f8fafc',
                border: 'none',
                borderBottom: expandedSchemas[schema.name] ? '1px solid #e2e8f0' : 'none',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                textAlign: 'left',
              }}
            >
              <div>
                <strong style={{ color: '#1e293b', fontSize: '16px' }}>{schema.name}</strong>
                <span style={{ marginLeft: '12px', color: '#64748b', fontSize: '14px' }}>
                  {schema.tableCount} tablo
                </span>
              </div>
              <span style={{ color: '#64748b', fontSize: '20px' }}>
                {expandedSchemas[schema.name] ? '▼' : '▶'}
              </span>
            </button>

            {expandedSchemas[schema.name] && (
              <div style={{ padding: '16px' }}>
                {filteredTables.map((table, idx) => (
                  <div
                    key={idx}
                    style={{
                      padding: '12px',
                      marginBottom: '8px',
                      background: '#f8fafc',
                      borderRadius: '6px',
                      border: '1px solid #e2e8f0',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#f1f5f9';
                      e.currentTarget.style.borderColor = '#3b82f6';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#f8fafc';
                      e.currentTarget.style.borderColor = '#e2e8f0';
                    }}
                    onClick={() => loadTableData(schema.name, table.name)}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <strong style={{ color: '#1e293b' }}>{table.name}</strong>
                      <div style={{ display: 'flex', gap: '8px', fontSize: '12px', color: '#64748b' }}>
                        {table.rowCount > 0 && (
                          <span>{table.rowCount.toLocaleString()} kayıt</span>
                        )}
                        <span>{table.columnCount} kolon</span>
                        <span style={{ color: '#3b82f6', fontWeight: '500' }}>Tıklayın</span>
                      </div>
                    </div>
                    
                    {/* Veri türü etiketleri */}
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '8px' }}>
                      {table.hasParkData && (
                        <span style={{
                          background: '#fef3c7',
                          color: '#92400e',
                          padding: '2px 8px',
                          borderRadius: '4px',
                          fontSize: '11px',
                        }}>Park</span>
                      )}
                      {table.hasGreenAreaData && (
                        <span style={{
                          background: '#d1fae5',
                          color: '#065f46',
                          padding: '2px 8px',
                          borderRadius: '4px',
                          fontSize: '11px',
                        }}>Yeşil Alan</span>
                      )}
                      {table.hasGeometryData && (
                        <span style={{
                          background: '#e9d5ff',
                          color: '#6b21a8',
                          padding: '2px 8px',
                          borderRadius: '4px',
                          fontSize: '11px',
                        }}>Şekil</span>
                      )}
                      {table.hasCoordinatesData && (
                        <span style={{
                          background: '#dbeafe',
                          color: '#1e40af',
                          padding: '2px 8px',
                          borderRadius: '4px',
                          fontSize: '11px',
                        }}>Koordinat</span>
                      )}
                      {table.hasWaterData && (
                        <span style={{
                          background: '#cffafe',
                          color: '#164e63',
                          padding: '2px 8px',
                          borderRadius: '4px',
                          fontSize: '11px',
                        }}>Su</span>
                      )}
                    </div>

                    {/* İlk birkaç kolon */}
                    {table.columns.length > 0 && (
                      <div style={{ fontSize: '12px', color: '#64748b', marginTop: '8px' }}>
                        <strong>Kolonlar:</strong> {table.columns.slice(0, 10).map(col => col.name).join(', ')}
                        {table.columns.length > 10 && ' ...'}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )})}
      </div>

      {/* Tablo Verisi Modal */}
      {selectedTable && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px',
          }}
          onClick={closeTableData}
        >
          <div
            style={{
              background: '#ffffff',
              borderRadius: '12px',
              width: '100%',
              maxWidth: '1400px',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{
              padding: '20px 24px',
              borderBottom: '1px solid #e2e8f0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              <div>
                <h3 style={{ margin: 0, color: '#1e293b', fontSize: '20px' }}>
                  {selectedTable.schema}.{selectedTable.table}
                </h3>
                {tableData && (
                  <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: '14px' }}>
                    Toplam {tableData.totalCount.toLocaleString()} kayıt
                  </p>
                )}
              </div>
              <button
                onClick={closeTableData}
                style={{
                  background: '#ef4444',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '8px 16px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                }}
              >
                ✕ Kapat
              </button>
            </div>

            {/* Modal Content */}
            <div style={{
              flex: 1,
              overflow: 'auto',
              padding: '24px',
            }}>
              {loadingTableData ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <div className={styles.spinner}></div>
                  <p style={{ marginTop: '20px', color: '#64748b' }}>Veriler yükleniyor...</p>
                </div>
              ) : tableData && tableData.rows.length > 0 ? (
                <>
                  {/* Tablo */}
                  <div style={{
                    overflowX: 'auto',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                  }}>
                    <table style={{
                      width: '100%',
                      borderCollapse: 'collapse',
                      fontSize: '13px',
                    }}>
                      <thead>
                        <tr style={{
                          background: '#f8fafc',
                          borderBottom: '2px solid #e2e8f0',
                        }}>
                          {tableData.columns.map((col, idx) => (
                            <th
                              key={idx}
                              style={{
                                padding: '12px',
                                textAlign: 'left',
                                fontWeight: '600',
                                color: '#1e293b',
                                borderRight: '1px solid #e2e8f0',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {col}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {tableData.rows.map((row, rowIdx) => (
                          <tr
                            key={rowIdx}
                            style={{
                              borderBottom: '1px solid #e2e8f0',
                              transition: 'background 0.2s',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = '#f8fafc';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = '#ffffff';
                            }}
                          >
                            {tableData.columns.map((col, colIdx) => (
                              <td
                                key={colIdx}
                                style={{
                                  padding: '12px',
                                  borderRight: '1px solid #e2e8f0',
                                  color: '#64748b',
                                  maxWidth: '300px',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                                title={row[col] ? String(row[col]) : ''}
                              >
                                {row[col] !== null && row[col] !== undefined
                                  ? String(row[col]).length > 50
                                    ? String(row[col]).substring(0, 50) + '...'
                                    : String(row[col])
                                  : <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>null</span>
                                }
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {tableData.totalCount > tableDataPageSize && (
                    <div style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: '12px',
                      marginTop: '20px',
                      paddingTop: '20px',
                      borderTop: '1px solid #e2e8f0',
                    }}>
                      <button
                        onClick={() => setTableDataPage(prev => Math.max(0, prev - 1))}
                        disabled={tableDataPage === 0}
                        style={{
                          padding: '8px 16px',
                          backgroundColor: tableDataPage === 0 ? '#e2e8f0' : '#3b82f6',
                          color: tableDataPage === 0 ? '#94a3b8' : '#ffffff',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: tableDataPage === 0 ? 'not-allowed' : 'pointer',
                          fontSize: '14px',
                          fontWeight: '500',
                        }}
                      >
                        ← Önceki
                      </button>
                      
                      <span style={{
                        color: '#64748b',
                        fontSize: '14px',
                        fontWeight: '500',
                        minWidth: '150px',
                        textAlign: 'center',
                      }}>
                        Sayfa {tableDataPage + 1} / {Math.ceil(tableData.totalCount / tableDataPageSize)}
                        {' '}({tableDataPage * tableDataPageSize + 1} - {Math.min((tableDataPage + 1) * tableDataPageSize, tableData.totalCount)} / {tableData.totalCount})
                      </span>
                      
                      <button
                        onClick={() => setTableDataPage(prev => prev + 1)}
                        disabled={tableDataPage >= Math.ceil(tableData.totalCount / tableDataPageSize) - 1}
                        style={{
                          padding: '8px 16px',
                          backgroundColor: tableDataPage >= Math.ceil(tableData.totalCount / tableDataPageSize) - 1 ? '#e2e8f0' : '#3b82f6',
                          color: tableDataPage >= Math.ceil(tableData.totalCount / tableDataPageSize) - 1 ? '#94a3b8' : '#ffffff',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: tableDataPage >= Math.ceil(tableData.totalCount / tableDataPageSize) - 1 ? 'not-allowed' : 'pointer',
                          fontSize: '14px',
                          fontWeight: '500',
                        }}
                      >
                        Sonraki →
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                  {loadingTableData ? (
                    <>
                      <div className={styles.spinner} style={{ margin: '0 auto 20px' }}></div>
                      <p>Veriler yükleniyor...</p>
                    </>
                  ) : (
                    <>
                      <p style={{ fontSize: '16px', marginBottom: '12px' }}>
                        {tableData && tableData.totalCount === 0 
                          ? 'Bu tabloda veri bulunamadı.' 
                          : 'Veri yüklenemedi.'}
                      </p>
                      {tableData && tableData.totalCount === 0 && (
                        <p style={{ fontSize: '13px', color: '#94a3b8' }}>
                          Tablo mevcut ancak içinde kayıt yok.
                        </p>
                      )}
                      {!tableData && (
                        <p style={{ fontSize: '13px', color: '#94a3b8' }}>
                          Lütfen tarayıcı konsolunu kontrol edin (F12) veya backend loglarını kontrol edin.
                        </p>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default DatabaseSummary;

